'use client';

import { c as _c } from "react/compiler-runtime";
import './stepper-tabbed.scss';
import * as Tabs from '@radix-ui/react-tabs';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/lib/analytics/events.ts';
import { SegmentedStepper } from '../../atoms/segmented-stepper/segmented-stepper.tsx';
import { cn } from '../../utils/helpers.ts';
import { StepperCompact } from '../stepper-compact/stepper-compact.tsx';
export type StepperTabbedProps = {
  title?: string;
  items?: Array<FlexibleContentProps>;
  /** When set, tabs beyond this index cannot be clicked. */
  maxStep?: number;
  /** Controlled active step index. When provided, the component does not
   *  manage its own step via the URL query string. */
  activeStep?: number;
  /** Callback fired when the user clicks a tab (controlled mode). */
  onStepChange?: (step: number) => void;
};

/**
 * Shared rendering logic for both controlled and uncontrolled modes.
 */
const StepperTabbedInner: React.FC<Omit<StepperTabbedProps, 'activeStep' | 'onStepChange'> & {
  activeIndex: number;
  setActiveIndex: (next: number) => void;
}> = t0 => {
  const $ = _c(7);
  const {
    title,
    items,
    maxStep,
    activeIndex,
    setActiveIndex
  } = t0;
  const {
    t
  } = useTranslation("help-me");
  let t1;
  if ($[0] !== activeIndex || $[1] !== items || $[2] !== maxStep || $[3] !== setActiveIndex || $[4] !== t || $[5] !== title) {
    t1 = items && items.length > 0 && <Tabs.Root className="StepperTabbed" defaultValue="0" value={activeIndex.toString()} onValueChange={e => {
      const next = Number(e);
      if (maxStep !== undefined && next > maxStep) {
        return;
      }
      setActiveIndex(next);
    }}><div className="md:hidden"><StepperCompact items={items} /></div><Tabs.List className="StepperTabbed__list hidden md:flex justify-between mx-auto" aria-label={title || t("skippy:StepperTabbed.defaultAriaLabel")}>{items.map((item, index) => <Tabs.Trigger className="flex flex-1 justify-center align-center mx-auto gap-snug-xl px-comfy-md py-comfy-xs label-potent label-lg" value={index.toString()} key={item.title} disabled={maxStep !== undefined && index > maxStep} onClick={() => {
          if (maxStep !== undefined && index > maxStep) {
            return;
          }
          setActiveIndex(index);
          trackClick(`${index + 1}. ${item.title}`, undefined, "Stepper__desktop");
        }}><span className="hidden md:flex">{activeIndex > index && <span className="flex align-center px-snug-xl"><ArkIcon iconName="check-circle" className="StepperTabbed__icon" /></span>}<span className={cn("label-potent label-lg", {
              "StepperTabbed__button--active": activeIndex >= index
            })}>{activeIndex <= index ? `${index + 1}. ${item.title}` : item.title}</span></span></Tabs.Trigger>)}</Tabs.List><SegmentedStepper count={items.length} activeItem={activeIndex} className="StepperTabbed__stepper" /><div className="StepperTabbed__divider" />{items.map(_temp)}</Tabs.Root>;
    $[0] = activeIndex;
    $[1] = items;
    $[2] = maxStep;
    $[3] = setActiveIndex;
    $[4] = t;
    $[5] = title;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};

/**
 * Uncontrolled variant that manages step via the URL query string (`?step=`).
 */
const StepperTabbedUncontrolled: React.FC<Omit<StepperTabbedProps, 'activeStep' | 'onStepChange'>> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsInteger.withDefault(0);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [queryStep, setQueryStep] = useQueryState("step", t0);
  let t1;
  if ($[1] !== props || $[2] !== queryStep || $[3] !== setQueryStep) {
    t1 = <StepperTabbedInner {...props} activeIndex={queryStep} setActiveIndex={setQueryStep} />;
    $[1] = props;
    $[2] = queryStep;
    $[3] = setQueryStep;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

/**
 * Controlled variant that uses the provided `activeStep` and `onStepChange`.
 */
const StepperTabbedControlled: React.FC<Omit<StepperTabbedProps, 'activeStep' | 'onStepChange'> & {
  activeStep: number;
  onStepChange?: (step: number) => void;
}> = t0 => {
  const $ = _c(10);
  let activeStep;
  let onStepChange;
  let props;
  if ($[0] !== t0) {
    ({
      activeStep,
      onStepChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = activeStep;
    $[2] = onStepChange;
    $[3] = props;
  } else {
    activeStep = $[1];
    onStepChange = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== onStepChange) {
    t1 = next => onStepChange?.(next);
    $[4] = onStepChange;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== activeStep || $[7] !== props || $[8] !== t1) {
    t2 = <StepperTabbedInner {...props} activeIndex={activeStep} setActiveIndex={t1} />;
    $[6] = activeStep;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const StepperTabbed: React.FC<StepperTabbedProps> = t0 => {
  const $ = _c(10);
  let activeStep;
  let onStepChange;
  let rest;
  if ($[0] !== t0) {
    ({
      activeStep,
      onStepChange,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = activeStep;
    $[2] = onStepChange;
    $[3] = rest;
  } else {
    activeStep = $[1];
    onStepChange = $[2];
    rest = $[3];
  }
  if (activeStep !== undefined) {
    let t1;
    if ($[4] !== activeStep || $[5] !== onStepChange || $[6] !== rest) {
      t1 = <StepperTabbedControlled {...rest} activeStep={activeStep} onStepChange={onStepChange} />;
      $[4] = activeStep;
      $[5] = onStepChange;
      $[6] = rest;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t1;
  if ($[8] !== rest) {
    t1 = <StepperTabbedUncontrolled {...rest} />;
    $[8] = rest;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
};
function _temp(item_0, index_0) {
  return <Tabs.Content className="StepperTabbed__content pt-comfy-lg @lg:pt-comfy-xl align-center justify-around m-auto" value={index_0.toString()} key={item_0.title}>{item_0.body}</Tabs.Content>;
}